const article = {
  slug: "words-that-sell-a-house",
  cluster: "seller",
  title: "Words That Sell a House (and the Words That Cost You Money)",
  description:
    "Words that sell a house vs. words that quietly cost you money. Two contrast tables, why concrete specifics beat vague adjectives, and how to fix weak listing copy.",
  audience: "Sellers & agents",
  funnelStage: "TOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "6 min read",
  tldr: 'The words that sell a house are concrete and specific: they name features, materials, and benefits buyers can picture and verify ("quartz counters," "south-facing yard," "10-minute walk to the train"). The words that cost you money are vague or coded apology words ("cozy," "TLC," "fixer," "as-is," "must-see") that either say nothing or signal problems. Swap adjectives for evidence.',
  blocks: [
    {
      type: "p",
      spans: [
        "The words that sell a house are the concrete, specific ones — words that name a real feature, material, or benefit a buyer can picture and verify. The words that cost you money are vague adjectives and coded apology words that either say nothing or quietly signal a problem. If you change nothing else about your listing description, replace empty praise with specific facts and your copy will work harder for you.",
      ],
    },
    {
      type: "callout",
      spans: [
        'Rule of thumb: a word earns its place in your listing only if a buyer can picture it or fact-check it. "Stunning" fails both tests. "Two walls of south-facing windows" passes both.',
      ],
    },
    {
      type: "h2",
      text: "What words actually sell a house?",
      id: "words-that-sell",
    },
    {
      type: "p",
      spans: [
        'Buyers don\'t get excited by adjectives — they get excited by things they can imagine living with. The strongest listing language does one of three jobs: it names a specific feature, it quantifies something (size, distance, year, count), or it describes a concrete benefit. "Updated kitchen" is forgettable. "Kitchen remodeled in 2022 with quartz counters and a gas range" gives a buyer something real to hold onto.',
      ],
    },
    {
      type: "table",
      headers: ["Instead of (vague)", "Use (specific and concrete)", "Why it works"],
      rows: [
        [
          "Stunning kitchen",
          "Quartz counters, gas range, walk-in pantry",
          "Names features buyers value and can verify",
        ],
        [
          "Great location",
          "8-minute walk to the train; corner lot on a quiet street",
          "Distance and street type are checkable facts",
        ],
        [
          "Spacious",
          "Open-plan living/dining; 9-foot ceilings",
          "Specifics let buyers picture furniture fitting",
        ],
        [
          "Lots of natural light",
          "South-facing windows across the main floor",
          "Direction signals real, all-day light",
        ],
        [
          "Move-in ready",
          "New roof (2023), updated electrical, fresh interior paint",
          "Reduces perceived risk and future cost",
        ],
        [
          "Beautiful backyard",
          "Fenced yard, mature shade trees, stone patio",
          "Concrete features support outdoor-living dreams",
        ],
        [
          "Plenty of storage",
          "Walk-in closet in primary; built-in garage shelving",
          "Storage is a top buyer priority when made tangible",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "Notice the pattern: every word on the right side is either a noun (a thing) or a number (a fact). That's the whole trick. For a full walkthrough of structuring the description around these, see ",
        {
          text: "how to write a listing description that sells",
          href: "/resources/how-to-write-a-listing-description-that-sells",
        },
        ".",
      ],
    },
    {
      type: "h2",
      text: "What words quietly cost you money?",
      id: "words-that-cost-money",
    },
    {
      type: "p",
      spans: [
        'Some words don\'t just fail to help — they actively scare buyers off or invite lowball offers. The worst offenders fall into two groups: vague filler that signals you have nothing specific to say, and coded "apology" words that experienced buyers (and their agents) read as warning labels.',
      ],
    },
    {
      type: "table",
      headers: ["Word or phrase", "What you mean", "What the buyer hears"],
      rows: [
        ["Cozy", "Warm and inviting", "Small and cramped"],
        ["Quaint / charming", "Full of character", "Old and possibly dated"],
        ["Cosmetic updates needed", "Just paint and finishes", "Who knows what's behind the walls"],
        ["TLC / needs love", "A little work", "A project I'll overpay for"],
        ["Fixer / handyman special", "Priced for renovation", "Major problems, bring a contractor"],
        ["As-is", "I won't renegotiate after inspection", "Something is wrong and they know it"],
        ["Motivated seller", "Open to offers", "Desperate — start low"],
        ["Must-see / won't last", "I'm excited about it", "Empty hype, no real information"],
      ],
    },
    {
      type: "p",
      spans: [
        'Words like "fixer," "TLC," and "cosmetic" are especially expensive because they prime buyers to subtract. Once a buyer decides a home is a project, they don\'t just discount for the actual repair — they pad their estimate for the unknown. If real work is needed, it\'s almost always better to be specific ("original kitchen, ready for your updates; recent roof and furnace") than to use a fuzzy code word that lets the buyer imagine the worst.',
      ],
    },
    {
      type: "callout",
      spans: [
        "Vague negatives invite vague (low) offers. A buyer who can't tell how much work a home needs will assume the most expensive version of the truth.",
      ],
    },
    {
      type: "h2",
      text: "Why do concrete specifics beat adjectives?",
      id: "specifics-beat-adjectives",
    },
    {
      type: "p",
      spans: ["Three reasons specifics win, every time:"],
    },
    {
      type: "ul",
      items: [
        [
          'They\'re believable. Every listing claims to be "beautiful" and "stunning," so those words have stopped meaning anything. A specific detail like "original 1920s hardwood" reads as true because it\'s the kind of thing you can only know by looking.',
        ],
        [
          'They help the right buyer self-select. "Home office with a door and a window" or "first-floor bedroom and full bath" tells a specific buyer this house solves their specific problem. Generic praise reaches no one in particular.',
        ],
        [
          "They do the buyer's imagining for them. People buy when they can picture their life in the space. Concrete nouns — patio, pantry, mudroom, fenced yard — paint that picture. Adjectives just assert that it's nice.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "This is also why a short list of strong, specific words outperforms a long pile of praise. If you're hunting for the right ones, our roundup of ",
        {
          text: "words that sell a house",
          href: "/resources/words-that-sell-a-house",
        },
        " feeds into the broader question of ",
        {
          text: "why isn't my house selling",
          href: "/resources/why-isnt-my-house-selling",
        },
        " — and weak description language is one of the most common, most fixable culprits.",
      ],
    },
    {
      type: "h2",
      text: "How do I fix a description that's full of weak words?",
      id: "how-to-fix",
    },
    {
      type: "p",
      spans: ["You don't need to be a copywriter. Work through your draft with this simple pass:"],
    },
    {
      type: "ol",
      items: [
        [
          'Circle every adjective (stunning, charming, spacious, beautiful). For each one, ask: can I replace this with a fact? "Spacious primary" becomes "primary fits a king bed with room for a reading chair."',
        ],
        [
          "Delete every hype word that carries no information: must-see, won't last, dream home, one-of-a-kind. They take up space the buyer skims past.",
        ],
        [
          "Hunt for apology code words (cozy, TLC, fixer, cosmetic, as-is). Either cut them or replace them with the specific, honest fact underneath.",
        ],
        [
          'Lead with your single best concrete feature, not a vague welcome line. "Renovated 2021 kitchen opens to a fenced backyard" beats "Welcome home to this charming gem."',
        ],
        [
          "Read it back as a skeptical buyer. Anything you couldn't verify or picture is a candidate to cut.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "If you'd rather not do this by hand, our ",
        {
          text: "listing description generator",
          href: "/tools/listing-description-generator",
        },
        " turns your home's real features into specific, buyer-ready copy, and a full ",
        {
          text: "Listino listing prep",
          href: "/get-started",
        },
        " rewrites the description you already have — vague and self-sabotaging words replaced before you relist.",
      ],
    },
    {
      type: "stat",
      value: "Cut the praise, keep the proof",
      label: "The single best edit you can make to a listing description",
    },
  ],
  faqs: [
    {
      question: "What are the best words to use in a listing description?",
      answer: [
        {
          type: "p",
          spans: [
            'The best words are concrete and specific: ones that name a real feature (quartz counters, walk-in pantry, fenced yard), quantify something (8-minute walk to the train, 9-foot ceilings, new roof in 2023), or describe a clear benefit. Specific nouns and numbers are believable and let buyers picture living there — generic adjectives like "stunning" and "charming" have lost all meaning because every listing uses them.',
          ],
        },
      ],
    },
    {
      question: "What words should I avoid in a home listing?",
      answer: [
        {
          type: "p",
          spans: [
            "Avoid vague hype (must-see, stunning, dream home, won't last) because it carries no information, and avoid coded \"apology\" words that signal problems: cozy (reads as cramped), TLC and fixer and handyman special (reads as expensive project), cosmetic updates needed (reads as hidden issues), and as-is (reads as something's wrong). If real work is needed, be specific and honest instead of using a fuzzy code word that lets buyers imagine the worst.",
          ],
        },
      ],
    },
    {
      question: 'Does the word "cozy" really hurt a listing?',
      answer: [
        {
          type: "p",
          spans: [
            'Yes. "Cozy" is widely read by buyers and agents as code for small or cramped. The same goes for "quaint" and "charming," which often translate to old or dated. If a room genuinely is compact, it\'s better to describe what it does well — "efficient galley kitchen" or "sunny breakfast nook" — than to use a word buyers have learned to distrust.',
          ],
        },
      ],
    },
    {
      question: 'Should I ever describe a house as a "fixer" or needing "TLC"?',
      answer: [
        {
          type: "p",
          spans: [
            'It\'s usually better to be specific than to use these code words. "Fixer" and "TLC" make buyers assume major, unknown problems and pad their lowball offers accordingly. Instead, state the actual condition plainly: note what\'s original or dated and what\'s already been updated ("original kitchen and baths; recent roof, furnace, and electrical"). Honest specifics reduce perceived risk; vague negatives amplify it.',
          ],
        },
      ],
    },
    {
      question: "Do listing description words actually affect the sale price?",
      answer: [
        {
          type: "p",
          spans: [
            "They can. Description language doesn't change a home's value, but it shapes how buyers perceive risk and how excited they get — both of which influence whether they offer, how fast, and how high. Words that signal an unknown amount of work (fixer, TLC, cosmetic, as-is) invite cautious, lower offers, while concrete specifics build confidence. It's one of the cheapest, fastest levers you can pull before relisting.",
          ],
        },
      ],
    },
  ],
  related: ["how-to-write-a-listing-description-that-sells", "why-isnt-my-house-selling"],
};

export default article;
